/* simulations-gift-trust.js — gift trust capture + multi-step submit.
   A reusable createCapture(pane) wires the live photo/clue camera + signature
   pad + live preview for any pane that has [data-gift-mark] + [data-gift-preview].
     · PERSONAL  : Recipients → Personalise (mark) → Pay → /api/gift/initiate
     · BULK/COMPANY: Setup → [Mark, email-each only] → Review → /api/gift/bulk-or-company
   The existing form mechanics (recipient rows, volume echo, CSV parse, validation)
   stay in simulations-gift.js; this file owns capture + the step state machines. */
(function () {
  "use strict";
  var shell = document.querySelector('.sim-gift-shell');
  if (!shell) return;

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Currency-correct totals (visitor's presentment currency from Liquid).
  var UNIT = (parseInt(shell.getAttribute('data-gift-cents'), 10) || 30000) / 100;
  var CUR = shell.getAttribute('data-gift-currency') || 'USD';
  var MFMT; try { MFMT = new Intl.NumberFormat(undefined, { style: 'currency', currency: CUR, minimumFractionDigits: 0, maximumFractionDigits: 0 }); } catch (e) { MFMT = null; }
  function money(amount) { return MFMT ? MFMT.format(amount || 0) : '$' + Math.round(amount || 0).toLocaleString('en-US'); }
  var PV_FACE = 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160"><rect width="100%" height="100%" fill="#e9e9e9"/><circle cx="80" cy="62" r="30" fill="#bcbcbc"/><path d="M26 150c0-34 24-54 54-54s54 20 54 54z" fill="#bcbcbc"/></svg>');
  var PV_CLUE = 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="140" height="104"><rect width="100%" height="100%" fill="#ededed"/><text x="50%" y="54%" font-size="11" fill="#bbb" text-anchor="middle" font-family="sans-serif">your clue</text></svg>');

  /* ── reusable trust capture (live photo/clue + signature + live preview) ──
     opts.nameFn() → the "from" display name for the preview dedication.
     opts.noteFn() → the note line for the preview (personal: first recipient). */
  function createCapture(pane, opts) {
    var mark = pane.querySelector('[data-gift-mark]');
    if (!mark) return null;
    opts = opts || {};
    var nameFn = opts.nameFn || function () { return ''; };
    var noteFn = opts.noteFn || function () { return ''; };
    var q = function (sel) { return mark.querySelector(sel); };
    var anon = false, photoData = null, stream = null, ready = false, pendingSig = null;
    var changed = function () { if (opts.onChange) opts.onChange(); };
    var off = document.createElement('canvas');
    var cam = q('[data-mark-cam]'), video = q('[data-mark-video]'), shot = q('[data-mark-shot]');
    var pv = pane.querySelector('[data-gift-preview]');
    var pad = pane.querySelector('[data-mark-sig]'), ctx = pad.getContext('2d'), drawing = false, signed = false;

    function stopStream() { if (stream) { stream.getTracks().forEach(function (t) { t.stop(); }); stream = null; } }

    function renderPreview() {
      if (!pv) return;
      var name = (nameFn() || '').trim(), note = (noteFn() || '').trim();
      var av = pv.querySelector('[data-pv-avatar]');
      if (av) { av.src = photoData || (anon ? PV_CLUE : PV_FACE); av.classList.toggle('is-clue', anon); }
      var clue = pv.querySelector('[data-pv-clue]'); if (clue) clue.hidden = !anon;
      var ded = pv.querySelector('[data-pv-ded]');
      if (ded) ded.textContent = anon ? 'A friend wanted you to have this Simulation.'
        : (name ? name + ' wanted you to have this Simulation.' : 'wanted you to have this Simulation.');
      var noteEl = pv.querySelector('[data-pv-note]');
      if (noteEl) { if (note) { noteEl.textContent = '“' + note + '”'; noteEl.hidden = false; } else { noteEl.hidden = true; } }
      var sigEl = pv.querySelector('[data-pv-sig]');
      if (sigEl) { if (signed) { sigEl.src = pendingSig || pad.toDataURL(); sigEl.hidden = false; } else { sigEl.hidden = true; } }
    }

    function setMode(a) {
      anon = a;
      q('[data-mark-named]').setAttribute('aria-pressed', !a);
      q('[data-mark-anon]').setAttribute('aria-pressed', a);
      q('[data-mark-hint]').innerHTML = a
        ? 'No Live Photo. Leave a clue instead. It proves you’re real, and keeps the mystery. <span class="t-req">*</span>'
        : 'A Live Photo proves it’s really you. Your face appears on the gift they open. <span class="t-req">*</span>';
      var ph = q('[data-mark-ph]'); if (ph) ph.innerHTML = a ? 'Photo a clue<br>allow access, or upload' : 'Camera preview<br>allow access, or upload';
      cam.classList.toggle('is-clue', a);
      cam.classList.toggle('is-selfie', !a);
      var sum = pane.querySelector('[data-rv-attribution]');
      if (sum) sum.innerHTML = a
        ? 'Anonymous. Your clue and signature travel with the gift. They see only &ldquo;a friend.&rdquo;'
        : 'From you, named. Your photo and signature travel with the gift.';
      renderPreview(); changed();
    }
    q('[data-mark-named]').addEventListener('click', function () { setMode(false); });
    q('[data-mark-anon]').addEventListener('click', function () { setMode(true); });

    // ── camera ──
    function startCam() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return fallback();
      stopStream();
      navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1280 } } })
        .then(function (s) {
          stream = s; video.srcObject = s;
          video.onloadedmetadata = function () { video.play(); ready = true; };
          cam.dataset.cam = 'live';
          q('[data-mark-start]').hidden = true; q('[data-mark-retake]').hidden = true; q('[data-mark-capture]').hidden = false;
        }).catch(fallback);
    }
    function fallback() { q('[data-mark-ph]').innerHTML = 'Camera needs HTTPS or permission.<br>Use “Upload” instead.'; cam.dataset.cam = 'idle'; }
    q('[data-mark-start]').addEventListener('click', startCam);
    q('[data-mark-capture]').addEventListener('click', function () {
      if (!ready || !video.videoWidth) return;
      var w = video.videoWidth, h = video.videoHeight; off.width = w; off.height = h;
      var c = off.getContext('2d');
      if (!anon) { c.save(); c.translate(w, 0); c.scale(-1, 1); }
      c.drawImage(video, 0, 0, w, h); if (!anon) c.restore();
      photoData = off.toDataURL('image/jpeg', 0.82);
      shot.src = photoData; cam.dataset.cam = 'shot'; stopStream(); ready = false;
      q('[data-mark-capture]').hidden = true; q('[data-mark-retake]').hidden = false; q('[data-mark-start]').hidden = true;
      renderPreview(); changed();
    });
    q('[data-mark-retake]').addEventListener('click', function () { photoData = null; renderPreview(); changed(); startCam(); });

    // Downscale uploads to ≤1280px / JPEG 0.82 so they reliably land under the
    // Worker's 3MB R2 cap — an un-resized phone photo (8MB+) would otherwise be
    // silently dropped server-side and the gift would send with no face/clue.
    // Falls back to the raw data URL if the image can't be decoded (e.g. HEIC).
    function downscale(file, cb) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        URL.revokeObjectURL(url);
        var max = 1280, w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
        if (Math.max(w, h) > max) { var s = max / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s); }
        var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
        cv.getContext('2d').drawImage(img, 0, 0, w, h);
        try { cb(cv.toDataURL('image/jpeg', 0.82)); }
        catch (e) { var fr = new FileReader(); fr.onload = function () { cb(fr.result); }; fr.readAsDataURL(file); }
      };
      img.onerror = function () { URL.revokeObjectURL(url); var fr = new FileReader(); fr.onload = function () { cb(fr.result); }; fr.readAsDataURL(file); };
      img.src = url;
    }
    q('[data-mark-upload]').addEventListener('change', function (e) {
      var f = e.target.files[0]; if (!f) return;
      downscale(f, function (data) {
        photoData = data; shot.src = photoData; cam.dataset.cam = 'shot'; stopStream();
        q('[data-mark-capture]').hidden = true; q('[data-mark-start]').hidden = false; q('[data-mark-retake]').hidden = false; renderPreview(); changed();
      });
    });

    // ── signature pad ──
    // The pad lives in a view that's display:none until reached, so a blind fit
    // on load measures 0×0. fitPad() no-ops while hidden and is (re)called on
    // view-entry + resize, preserving any stroke across a resize.
    function styleCtx() { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.scale(2, 2); ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#000'; }
    function fitPad() {
      var r = pad.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var sized = pad.width === Math.round(r.width * 2) && pad.height === Math.round(r.height * 2);
      if (sized && !pendingSig) return;
      var prev = pendingSig || (signed ? pad.toDataURL() : null);   // pendingSig = restored from memory
      pad.width = Math.round(r.width * 2); pad.height = Math.round(r.height * 2);
      styleCtx();
      if (prev) { var im = new Image(); im.onload = function () { ctx.drawImage(im, 0, 0, r.width, r.height); }; im.src = prev; }
      pendingSig = null;
    }
    var refitT;
    window.addEventListener('resize', function () { clearTimeout(refitT); refitT = setTimeout(fitPad, 150); });
    function pos(e) { var r = pad.getBoundingClientRect(); var t = e.touches ? e.touches[0] : e; return { x: t.clientX - r.left, y: t.clientY - r.top }; }
    var hint = pane.querySelector('[data-mark-sighint]');
    function down(e) { drawing = true; signed = true; if (hint) hint.style.display = 'none'; var p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); }
    function move(e) { if (!drawing) return; var p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); e.preventDefault(); renderPreview(); }
    function up() { var was = drawing; drawing = false; if (was) changed(); }
    pad.addEventListener('mousedown', down); pad.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
    pad.addEventListener('touchstart', down, { passive: false }); pad.addEventListener('touchmove', move, { passive: false }); pad.addEventListener('touchend', up);
    var clearBtn = pane.querySelector('[data-mark-clear]');
    if (clearBtn) clearBtn.addEventListener('click', function () { ctx.clearRect(0, 0, pad.width, pad.height); signed = false; pendingSig = null; if (hint) hint.style.display = ''; renderPreview(); changed(); });

    setMode(false);

    return {
      setMode: setMode, renderPreview: renderPreview, fitPad: fitPad, stopStream: stopStream,
      anon: function () { return anon; }, signed: function () { return signed; },
      photo: function () { return photoData; }, signature: function () { return pendingSig || pad.toDataURL('image/png'); },
      // restore from memory
      restorePhoto: function (d) { if (!d) return; photoData = d; shot.src = d; cam.dataset.cam = 'shot'; q('[data-mark-capture]').hidden = true; q('[data-mark-start]').hidden = false; q('[data-mark-retake]').hidden = false; renderPreview(); },
      restoreSig: function (d) { if (!d) return; signed = true; pendingSig = d; if (hint) hint.style.display = 'none'; fitPad(); renderPreview(); },
      // dev-previews: seed a placeholder face so the mark/preview render (no signature).
      seedDemo: function () {
        photoData = anon ? PV_CLUE : PV_FACE; shot.src = photoData; cam.dataset.cam = 'shot';
        q('[data-mark-capture]').hidden = true; q('[data-mark-start]').hidden = false; q('[data-mark-retake]').hidden = false;
        renderPreview();
      }
    };
  }

  /* ── PERSONAL flow: Recipients → Personalise → Pay ── */
  (function () {
    var pane = shell.querySelector('[data-gift-pane="personal"]');
    if (!pane || !pane.querySelector('[data-gift-mark]')) return;

    // ── form memory: persist everything to sessionStorage so browser-back from
    // checkout (or a refresh) keeps the recipients, note, photo and signature.
    // Survives the tab session; cleared when the tab closes (keeps a selfie private). ──
    var STORE = 'simgift:personal:v1', saveT, cap;
    function saveState() {
      if (!cap) return;
      clearTimeout(saveT);
      saveT = setTimeout(function () {
        try {
          var recipients = [];
          pane.querySelectorAll('[data-recipient-row]').forEach(function (row) {
            recipients.push({
              name: (row.querySelector('input[name$="[name]"]') || {}).value || '',
              email: (row.querySelector('input[name$="[email]"]') || {}).value || '',
              note: (row.querySelector('input[name$="[note]"]') || {}).value || ''
            });
          });
          sessionStorage.setItem(STORE, JSON.stringify({
            recipients: recipients,
            gname: (pane.querySelector('#g-name') || {}).value || '',
            gemail: (pane.querySelector('#g-email') || {}).value || '',
            anon: cap.anon(),
            photo: cap.photo() || '',
            sig: cap.signed() ? cap.signature() : ''
          }));
        } catch (e) {}
      }, 250);
    }

    cap = createCapture(pane, {
      nameFn: function () { return ((pane.querySelector('#g-name') || {}).value || '').trim(); },
      noteFn: function () { var n = pane.querySelector('[data-recipient-row] input[name$="[note]"]'); return n ? (n.value || '').trim() : ''; },
      onChange: saveState
    });
    if (!cap) return;

    function restoreState() {
      try {
        var raw = sessionStorage.getItem(STORE); if (!raw) return;
        var s = JSON.parse(raw); if (!s) return;
        var addBtn = pane.querySelector('#add-recipient');
        if (s.recipients && s.recipients.length) {
          for (var i = 1; i < s.recipients.length && i < 25; i++) { if (addBtn) addBtn.click(); }
          var rowEls = pane.querySelectorAll('[data-recipient-row]');
          s.recipients.forEach(function (r, i) {
            var row = rowEls[i]; if (!row) return;
            var nm = row.querySelector('input[name$="[name]"]'); if (nm) nm.value = r.name || '';
            var em = row.querySelector('input[name$="[email]"]'); if (em) em.value = r.email || '';
            var nt = row.querySelector('input[name$="[note]"]'); if (nt) nt.value = r.note || '';
          });
        }
        var gn = pane.querySelector('#g-name'); if (gn && s.gname) gn.value = s.gname;
        var ge = pane.querySelector('#g-email'); if (ge && s.gemail) ge.value = s.gemail;
        var f = pane.querySelector('#gift-form'); if (f) f.dispatchEvent(new Event('input', { bubbles: true })); // refresh subtotal/CTA
        if (s.anon) cap.setMode(true);
        if (s.photo) cap.restorePhoto(s.photo);
        if (s.sig) cap.restoreSig(s.sig);
      } catch (e) {}
    }
    var f0 = pane.querySelector('#gift-form');
    if (f0) { f0.addEventListener('input', saveState); f0.addEventListener('change', saveState); }
    restoreState();

    // gift.js validates the selection form and sets view='review'; we run AFTER it
    // and divert that to 'personalise' (the mark step).
    var form = pane.querySelector('#gift-form');
    if (form) form.addEventListener('submit', function () {
      if (pane.getAttribute('data-view') === 'review') {
        pane.setAttribute('data-view', 'personalise');
        cap.fitPad(); cap.renderPreview(); window.scrollTo(0, 0);
      } else {
        var ge = ((pane.querySelector('#g-email') || {}).value || '').trim().toLowerCase();
        var dupe = false;
        pane.querySelectorAll('[data-recipient-row] input[name$="[email]"]').forEach(function (i) {
          if (ge && (i.value || '').trim().toLowerCase() === ge) dupe = true;
        });
        if (dupe) alert("That recipient email is your own. Use a different address. To test, try yourname+gift@gmail.com, which lands in your inbox.");
      }
    });
    var psContinue = pane.querySelector('[data-ps-continue]');
    if (psContinue) psContinue.addEventListener('click', function () {
      if (!cap.photo()) return alert(cap.anon() ? 'Add a clue photo.' : 'Take a photo.');
      if (!cap.signed()) return alert('Sign your gift.');
      cap.stopStream();
      pane.setAttribute('data-view', 'review'); window.scrollTo(0, 0);
    });
    var psBack = pane.querySelector('[data-ps-back]');
    if (psBack) psBack.addEventListener('click', function () { cap.stopStream(); pane.setAttribute('data-view', 'form'); window.scrollTo(0, 0); });
    var rvBack = pane.querySelector('[data-rv-back]');
    if (rvBack) rvBack.addEventListener('click', function () { pane.setAttribute('data-view', 'personalise'); cap.fitPad(); window.scrollTo(0, 0); });

    var review = pane.querySelector('#gift-review');
    if (review) review.addEventListener('submit', function (e) {
      e.preventDefault();
      var gName = (pane.querySelector('#g-name') || {}).value || '';
      var gEmail = ((pane.querySelector('#g-email') || {}).value || '').trim().toLowerCase();
      var recipients = [];
      pane.querySelectorAll('[data-recipient-row]').forEach(function (row) {
        var n = (row.querySelector('input[name$="[name]"]') || {}).value || '';
        var em = ((row.querySelector('input[name$="[email]"]') || {}).value || '').trim().toLowerCase();
        var nt = (row.querySelector('input[name$="[note]"]') || {}).value || '';
        if (n.trim() && em) recipients.push({ name: n.trim(), email: em, note: nt.trim() });
      });
      var termsOk = !!(pane.querySelector('[data-gift-terms]') || {}).checked;
      function fail(msg) { pane.setAttribute('data-view', 'form'); alert(msg); }
      if (!cap.anon() && !gName.trim()) return fail('Add your name.');
      if (!EMAIL_RE.test(gEmail)) return fail('Add a valid email for yourself.');
      if (!recipients.length) return fail('Add at least one recipient.');
      if (recipients.some(function (r) { return !r.note; })) return fail('Add a personal note for each recipient.');
      if (!cap.photo()) return fail(cap.anon() ? 'Add a clue photo.' : 'Take a photo.');
      if (!cap.signed()) return fail('Sign your gift.');
      if (!termsOk) return fail('Please confirm the Terms to continue.');

      cap.stopStream();
      pane.setAttribute('data-view', 'submitting');
      var payload = {
        mode: 'personal', anonymous: cap.anon(),
        gifter: { name: gName.trim(), email: gEmail, photo: cap.photo(), signature: cap.signature() },
        recipients: recipients, terms_accepted: true, terms_version: 'gift-v1'
      };
      fetch('/api/gift/initiate', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (d && d.ok && d.checkout_url) { location.href = d.checkout_url; return; }
          pane.setAttribute('data-view', 'form');
          alert('Could not start checkout: ' + ((d && (d.error || d.detail)) || 'unknown error'));
        })
        .catch(function (err) { pane.setAttribute('data-view', 'form'); alert('Network error: ' + err.message); });
    });

    // dev-previews: ?preview=personalise jumps to the mark step with a seeded face.
    try {
      if (new URLSearchParams(location.search).get('preview') === 'personalise') {
        var gn = pane.querySelector('#g-name'); if (gn && !gn.value) gn.value = 'Alex Rivera';
        var rn = pane.querySelector('[data-recipient-row] input[name$="[note]"]'); if (rn && !rn.value) rn.value = 'Saw this. Thought of you.';
        cap.seedDemo();
        pane.setAttribute('data-view', 'personalise'); cap.fitPad(); cap.renderPreview();
      }
    } catch (e) {}
  })();

  /* ── BULK / COMPANY flow: Setup → [Mark, email-each only] → Review → invoice ── */
  ['bulk', 'company'].forEach(function (mode) {
    var pane = shell.querySelector('[data-gift-pane="' + mode + '"]');
    if (!pane) return;
    var form = pane.querySelector('#gift-form-' + mode);
    if (!form) return;
    var pfx = mode === 'bulk' ? 'b' : 'c';
    var val = function (id) { var el = pane.querySelector('#' + pfx + '-g-' + id); return el ? el.value.trim() : ''; };
    var gname = function () { return val('name'); };
    var gemail = function () { return val('email').toLowerCase(); };
    var gorg = function () { return val('org'); };
    var grole = function () { return val('role'); };
    var delivery = function () { var r = form.querySelector('input[name="delivery"]:checked'); return r ? r.value : 'email'; };
    var payment = function () { var r = form.querySelector('input[name="payment"]:checked'); return r ? r.value : 'wire'; };
    var vol = function () { var i = form.querySelector('[data-volume]'); return i ? (parseInt(i.value, 10) || 0) : 0; };
    var rows = function () { return pane.__giftCsvRows || []; };

    var cap = pane.querySelector('[data-gift-mark]') ? createCapture(pane, {
      nameFn: function () {
        var nm = gname(), org = gorg();
        // Company → "Name of Company"; Bulk → the individual (name first).
        if (mode === 'company' && nm && org) return nm + ' of ' + org;
        return nm || org;
      },
      noteFn: function () { return ''; }
    }) : null;

    function setView(v) { pane.setAttribute('data-view', v); window.scrollTo(0, 0); }
    function deliveryLabel(d) {
      return d === 'email' ? 'Email each directly'
        : d === 'self' ? 'Send codes to me, I’ll deliver'
        : 'No list, ' + vol() + ' gifts to distribute';
    }
    function paymentLabel(p) { return p === 'wire' ? 'Bank transfer / ACH' : p === 'po' ? 'Purchase order' : 'Card via Shopify checkout'; }
    function setText(sel, txt) { var el = pane.querySelector(sel); if (el) el.textContent = txt; }

    function buildReview() {
      var d = delivery(), n = d === 'bearer' ? vol() : (rows().length || vol());
      var from = gname() + (gorg() ? ' · ' + gorg() : '') + (grole() ? ' · ' + grole() : '') + ' · ' + gemail();
      setText('[data-bcrv-from]', from);
      setText('[data-bcrv-order]', n + ' × 001 Pastlife · ' + money(n * UNIT));
      setText('[data-bcrv-delivery]', deliveryLabel(d));
      setText('[data-bcrv-payment]', paymentLabel(payment()));
    }

    // Card pays upfront via Shopify checkout; wire/PO emails an invoice. Reflect
    // that in the final CTA + the last rail label so it isn't "Generate invoice"
    // for everything.
    function updatePaymentUI() {
      var card = payment() === 'card';
      var g = pane.querySelector('[data-bc-generate]');
      if (g) g.innerHTML = (card ? 'Continue to payment' : 'Generate invoice') + ' <span aria-hidden="true">&nbsp;→</span>';
      var d3 = pane.querySelector('.t-rail__step[data-step="3"] .t-rail__label');
      if (d3) d3.textContent = card ? 'Pay' : 'Invoice';
    }
    form.querySelectorAll('input[name="payment"]').forEach(function (r) { r.addEventListener('change', updatePaymentUI); });
    updatePaymentUI();

    // Setup → mark (email-each) or review.
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!gname()) return alert('Add your name.');
      if (!EMAIL_RE.test(gemail())) return alert('Add a valid email for yourself.');
      if (vol() < 1) return alert('Choose how many recipients.');
      var d = delivery();
      if (d !== 'bearer' && !rows().length) return alert('Upload your list (CSV) for this option. Or choose “No list” to get codes to distribute. (List upload is easiest on a computer.)');
      if (d === 'email' && cap) { setView('mark'); cap.fitPad(); cap.renderPreview(); }
      else { buildReview(); setView('review'); }
    });

    var mc = pane.querySelector('[data-bcm-continue]');
    if (mc) mc.addEventListener('click', function () {
      if (cap) {
        if (!cap.photo()) return alert(cap.anon() ? 'Add a clue photo.' : 'Take a photo.');
        if (!cap.signed()) return alert('Add your signature.');
        cap.stopStream();
      }
      buildReview(); setView('review');
    });
    var mb = pane.querySelector('[data-bcm-back]');
    if (mb) mb.addEventListener('click', function () { if (cap) cap.stopStream(); setView('setup'); });
    var rb = pane.querySelector('[data-bc-back]');
    if (rb) rb.addEventListener('click', function () {
      if (delivery() === 'email' && cap) { setView('mark'); cap.fitPad(); } else setView('setup');
    });

    var gen = pane.querySelector('[data-bc-generate]');
    if (gen) gen.addEventListener('click', function (e) {
      e.preventDefault();
      var terms = pane.querySelector('[data-bc-terms]');
      if (terms && !terms.checked) return alert('Please confirm the terms to continue.');
      var d = delivery();
      var dPayload = d === 'self' ? 'forward' : d;
      var payload = {
        mode: mode, anonymous: cap ? cap.anon() : false,
        gifter: {
          name: gname(), email: gemail(),
          company: gorg() || undefined, role: grole() || undefined,
          photo: (d === 'email' && cap) ? cap.photo() : undefined,
          signature: (d === 'email' && cap) ? cap.signature() : undefined
        },
        delivery: dPayload, payment: payment(), quantity: vol(),
        recipients: d === 'bearer' ? [] : rows(),
        terms_accepted: true, terms_version: 'gift-v1'
      };
      gen.disabled = true; gen.setAttribute('aria-disabled', 'true');
      fetch('/api/gift/bulk-or-company', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          gen.disabled = false; gen.setAttribute('aria-disabled', 'false');
          if (res && res.ok && res.checkout_url) { location.href = res.checkout_url; return; }
          if (res && res.ok) {
            var s = pane.querySelector('.t-success');
            if (s) {
              var id = s.querySelector('[data-bs-id]'); if (id) id.textContent = res.order_name || '#B-000';
              var line = s.querySelector('[data-bs-line]'); if (line) line.innerHTML = vol() + ' × 001 <em>Pastlife</em> · ' + money(vol() * UNIT);
            }
            setView('success'); return;
          }
          alert('Could not generate invoice: ' + ((res && (res.error || res.detail)) || 'unknown error'));
        })
        .catch(function (err) { gen.disabled = false; gen.setAttribute('aria-disabled', 'false'); alert('Network error: ' + err.message); });
    });

    if (cap) cap.setMode(false);
  });
})();
